import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import _ from "lodash";
import ACLStore from "lib/aclStore";
import {
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  ASSIGNMENTS_MANAGE_PERMISSION_IN_CALENDAR,
} from "Constants/permissionConstants";
import {
  getOrganizationCourseIdsHierarchyQuery,
  getUserCoursesV2HierarchyQuery,
} from "../modules/CoursesQueries";
import { buildHierarchyFromEdges } from "../modules/CoursesUtils";

/**
 * Phase 1 hook: Fetches all course/grade/school IDs in a single lightweight query,
 * then builds a client-side hierarchy grouping.
 *
 * For admins: uses org-level query filtered by courseFilters (supports curriculumProgramIds
 * for PROGRAM_LEVEL admins who can only see specific schools).
 * For non-admins: uses user-level coursesV2 query scoped to the staff member's own courses.
 *
 * @param {string} organizationId
 * @param {boolean} skip
 * @param {string} academicYearId
 * @param {Object} options
 * @param {string} options.userId - Required for non-admin path
 * @param {boolean} options.isAdmin - false = non-admin (use user courses query)
 * @param {Object} options.courseFilters - Extra filters for admin query (e.g. { curriculumProgramIds: [...] })
 */
const useCourseHierarchy = (
  organizationId,
  skip,
  academicYearId,
  { userId, isAdmin, courseFilters } = {}
) => {
  const isNonAdmin = isAdmin === false;

  const adminQueryResult = useQuery(getOrganizationCourseIdsHierarchyQuery, {
    variables: {
      organizationId,
      courseFilters: {
        ...courseFilters,
        ...(academicYearId ? { academicYears: [academicYearId] } : {}),
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !organizationId || skip || isNonAdmin,
    context: {
      useHttpLink: true,
    },
  });

  const nonAdminQueryResult = useQuery(getUserCoursesV2HierarchyQuery, {
    variables: {
      id: userId,
      userType: "STAFF",
      filters: {
        fetchOnlyMappedCourses: false,
        ...(academicYearId ? { academicYears: [academicYearId] } : {}),
        ...(ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)
          ? { controlParameters: [ASSIGNMENTS_MANAGE_PERMISSION_IN_CALENDAR] }
          : {}),
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !userId || skip || !isNonAdmin,
    context: {
      useHttpLink: true,
    },
  });

  const { data, loading } = isNonAdmin ? nonAdminQueryResult : adminQueryResult;

  const hierarchyData = useMemo(() => {
    const edges = isNonAdmin
      ? _.get(data, "node.coursesV2.edges", [])
      : _.get(data, "node.courses.edges", []);

    if (_.isEmpty(edges)) {
      return {
        hierarchy: {},
        allSchoolIds: [],
        allGradeIds: [],
        allCourseIds: [],
      };
    }

    return buildHierarchyFromEdges(edges);
  }, [data, isNonAdmin]);

  return {
    hierarchy: hierarchyData.hierarchy,
    allSchoolIds: hierarchyData.allSchoolIds,
    allGradeIds: hierarchyData.allGradeIds,
    allCourseIds: hierarchyData.allCourseIds,
    loading: loading && _.isEmpty(data),
    error: isNonAdmin ? nonAdminQueryResult.error : adminQueryResult.error,
  };
};

export default useCourseHierarchy;
